import './FanDetailModal.sass';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import format from 'date-fns/format';
import { fromUnixTime } from 'date-fns';
import { useDispatch } from 'react-redux';

import { Header, ModalWrapper, ScrollView } from '$uikit';
import { Loader, UnitName, UnitPhoto } from '$shared/components';
import { useTranslator, useUserInfo } from '$hooks';
import { CurrencyEnum } from '$store/models';
import { mainActions } from '$store/main';
import { goTo } from '$navigation/helper';
import { STATISTICS_PRO_LINKS } from '$shared/constants/pages';

import { fetchFanDetail } from '../StatisticsProFans.api';
import { FanDetailData } from '../StatisticsProFans.types';
import {
  DailyEarningRow,
  EarningsCategoryMeta,
  IncomeType,
  getAvailableCategoriesByCurrency,
} from '../StatisticsPro.types';
import { buildFrequencyBarOptions, formatAmount } from '../utils/fanCharts';
import { StatsChart } from './StatsChart';
import { CategoryFilter } from './CategoryFilter';

export interface FanDetailModalProps {
  userId: number;
  currency: CurrencyEnum;
  range: [number, number];
  onClose: () => void;
}

const DAY = 86400;
const toUtcDayStart = (unix: number) => Math.floor(unix / DAY) * DAY;

const MIN_AXIS_DAYS = 7;
function buildDailyAxis(
  activeDays: number[],
  rangeFrom: number,
  rangeTo: number,
): number[] {
  if (activeDays.length === 0) return [];
  const lo = toUtcDayStart(rangeFrom);
  const hi = toUtcDayStart(rangeTo);
  let start = Math.min(...activeDays);
  let end = Math.max(...activeDays);
  const span = Math.round((end - start) / DAY) + 1;
  if (span < MIN_AXIS_DAYS) {
    const pad = MIN_AXIS_DAYS - span;
    const before = Math.floor(pad / 2);
    start -= before * DAY;
    end += (pad - before) * DAY;
    if (start < lo) {
      end += lo - start;
      start = lo;
    }
    if (end > hi) {
      start -= end - hi;
      end = hi;
      if (start < lo) start = lo;
    }
  }
  const out: number[] = [];
  for (let d = start; d <= end; d += DAY) out.push(d);
  return out;
}

export const FanDetailModal: FC<FanDetailModalProps> = ({
  userId,
  currency,
  range,
  onClose,
}) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const user = useUserInfo(userId);

  const [detail, setDetail] = useState<FanDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchFanDetail(userId, {
      from: range[0],
      to: range[1],
      currency,
      sort: 'amount',
    })
      .then((data) => {
        if (!cancelled) {
          setDetail(data);
          if (data.user) dispatch(mainActions.setUsers({ [userId]: data.user }));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, currency, range, dispatch]);

  const ccy = currency.toUpperCase();
  const rangeLabel = `${format(fromUnixTime(range[0]), 'd MMM')} — ${format(
    fromUnixTime(range[1]),
    'd MMM',
  )}`;

  const barOptions = useMemo(
    () => (detail ? buildFrequencyBarOptions(detail.breakdown) : null),
    [detail],
  );
  const typesCount = useMemo(
    () => (detail ? Object.values(detail.breakdown).filter(Boolean).length : 0),
    [detail],
  );

  const { days, values, categories, labels } = useMemo(() => {
    if (!detail) {
      return {
        days: [] as number[],
        values: {} as Record<number, Partial<Record<IncomeType, number>>>,
        categories: [] as EarningsCategoryMeta[],
        labels: {} as Record<string, string>,
      };
    }
    const dailyRows: DailyEarningRow[] = detail.rows.map((row) => {
      const earnings: Record<IncomeType, number> = {};
      for (const [type, stat] of Object.entries(row.earnings)) {
        if (stat) earnings[type] = stat.amount;
      }
      return { date: row.date, currency, earnings };
    });
    const cats = getAvailableCategoriesByCurrency(dailyRows)[currency].map((cat) => ({
      ...cat,
      pinned: false,
    }));
    const valueMap: Record<number, Partial<Record<IncomeType, number>>> = {};
    for (const row of dailyRows) valueMap[row.date] = row.earnings;
    const labelMap: Record<string, string> = {};
    for (const cat of cats) labelMap[cat.type] = cat.label;
    return {
      days: buildDailyAxis(
        dailyRows.map((row) => row.date),
        range[0],
        range[1],
      ),
      values: valueMap,
      categories: cats,
      labels: labelMap,
    };
  }, [detail, currency, range]);

  const [selected, setSelected] = useState<IncomeType[]>([]);
  useEffect(() => {
    setSelected([]);
  }, [categories]);

  const toggleCategory = useCallback((type: IncomeType) => {
    setSelected((prev) =>
      prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type],
    );
  }, []);
  const resetCategories = useCallback(() => {
    setSelected([]);
  }, []);

  return (
    <ModalWrapper name={`stats_fan_${userId}`} onOverlayClick={onClose}>
      <Header showCloseButton backButtonOnClick={onClose}>
        <UnitName unit={user} />
      </Header>
      <ScrollView className="FanDetail">
        {isLoading || !detail ? (
          <Loader isWrapped />
        ) : (
          <>
            <div className="FanDetail__hero">
              <UnitPhoto unit={user} size="medium" />
              <div className="FanDetail__hero_info">
                <div className="FanDetail__hero_name">
                  <UnitName unit={user} />
                </div>
                <div className="FanDetail__hero_range">{rangeLabel}</div>
                {detail.promoLink && (
                  <div
                    className="FanDetail__hero_range FanDetail__hero_source"
                    onClick={() => {
                      onClose();
                      goTo(STATISTICS_PRO_LINKS, { linkId: detail.promoLink!.id });
                    }}
                  >
                    {t('statistics_pro_fans_detail_source', {
                      name: detail.promoLink.name,
                      defaultValue: 'Came via: %{name}',
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="FanDetail__stats">
              <div className="FanDetail__stat">
                <div className="FanDetail__stat_value">{detail.totalCount}</div>
                <div className="FanDetail__stat_label">
                  {t('statistics_pro_fans_stat_operations', {
                    defaultValue: 'Operations',
                  })}
                </div>
              </div>
              <div className="FanDetail__stat">
                <div className="FanDetail__stat_value">
                  {formatAmount(
                    detail.totalAmount / Math.max(detail.totalCount, 1),
                    currency,
                  )}{' '}
                  {ccy}
                </div>
                <div className="FanDetail__stat_label">
                  {t('statistics_pro_fans_stat_avg', { defaultValue: 'Avg / op' })}
                </div>
              </div>
              <div className="FanDetail__stat">
                <div className="FanDetail__stat_value">{typesCount}</div>
                <div className="FanDetail__stat_label">
                  {t('statistics_pro_fans_stat_types', { defaultValue: 'Types' })}
                </div>
              </div>
            </div>

            {detail.rows.length > 0 && (
              <>
                <div className="FanDetail__section_title">
                  {t('statistics_pro_fans_detail_timeline', {
                    defaultValue: 'Spending over time',
                  })}
                </div>
                <StatsChart
                  days={days}
                  values={values}
                  categories={categories}
                  selected={selected}
                  labels={labels}
                  currency={currency}
                />
                <CategoryFilter
                  categories={categories}
                  selected={selected}
                  onToggle={toggleCategory}
                  onReset={resetCategories}
                />
              </>
            )}

            <div className="FanDetail__section_title">
              {t('statistics_pro_fans_detail_frequency', {
                defaultValue: 'What they buy most often',
              })}
            </div>
            {barOptions && (
              <HighchartsReact highcharts={Highcharts} options={barOptions} immutable />
            )}
          </>
        )}
      </ScrollView>
    </ModalWrapper>
  );
};
